import { faBath } from '@fortawesome/pro-light-svg-icons/faBath';
import { faBedAlt } from '@fortawesome/pro-light-svg-icons/faBedAlt';
import { faCouch } from '@fortawesome/pro-light-svg-icons/faCouch';
import { faChartBar } from '@fortawesome/pro-regular-svg-icons/faChartBar';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { KeyInfo, Property } from '@propertypal/shared/src/types/property';
import { formatNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import { getStyleIcon } from '@propertypal/shared/src/utils/property/groupKeyInfo';
import puralise from '@propertypal/shared/src/utils/puralise';
import React, { FunctionComponent } from 'react';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import EnergyRatingIcon from '../property-box/EnergyRatingIcon';
import { Text } from '../typography';
import { Container, Item } from './PropertyInfoBar.style';

interface Props {
  property: Property;
  onClick: (info: KeyInfo) => void;
}

const STAT_ITEM: KeyInfo = {
  key: 'VIEW_PROPERTY_STATS',
  group: '',
  name: '',
  text: '',
  textHtml: '',
};

const getItems = (property: Property, onClick: Props['onClick']) => {
  const items = [];
  const epc = property.keyInfo?.find((i) => ['EPC_RATING', 'EPC_RATING_CHART', 'EPC_FILES'].includes(i.key));
  const ber = property.keyInfo?.find((i) => ['BER'].includes(i.key));
  const propertyStyle = property.keyInfo?.find((i) => ['STYLE', 'LAND_TYPE'].includes(i.key));

  if (propertyStyle) {
    items.push(
      <Item key="style">
        <FontAwesomeIcon icon={getStyleIcon(propertyStyle.text)} />

        <Text>{propertyStyle.text}</Text>
      </Item>,
    );
  }

  if (property.numBedrooms) {
    items.push(
      <Item key="bedrooms">
        <FontAwesomeIcon icon={faBedAlt} />

        <Text>
          {property.numBedrooms} {puralise('Bedroom', 'Bedrooms', property.numBedrooms)}
        </Text>
      </Item>,
    );
  }

  if (property.numBathrooms) {
    items.push(
      <Item key="bathrooms">
        <FontAwesomeIcon icon={faBath} />

        <Text>
          {property.numBathrooms} {puralise('Bathroom', 'Bathrooms', property.numBathrooms)}
        </Text>
      </Item>,
    );
  }

  if (property.numReceptionRooms) {
    items.push(
      <Item key="receptions">
        <FontAwesomeIcon icon={faCouch} />

        <Text>
          {property.numReceptionRooms} {puralise('Reception', 'Receptions', property.numReceptionRooms)}
        </Text>
      </Item>,
    );
  }

  if (epc) {
    items.push(
      <Item key="epc">
        <FontAwesomeIcon icon={faChartBar} />

        <button data-testid="epcText" type="button" onClick={() => onClick(epc)}>
          {epc.text}
        </button>
      </Item>,
    );
  }

  if (ber) {
    items.push(
      <Item key="epc">
        <FontAwesomeIcon icon={faChartBar} />

        <EnergyRatingIcon property={property} />
      </Item>,
    );
  }

  /* ensure property is published and is not a development property where we don't show any stats */
  if (
    property.stats &&
    property.published &&
    property.type === 'property' &&
    !property.development &&
    parseInt(property.stats.totalViews, 10) > 0
  ) {
    items.push(
      <Item key="totalViews">
        <FontAwesomeIcon icon={faEye} />
        <button type="button" onClick={() => onClick(STAT_ITEM)} data-testid="view-more-stats">
          {formatNumber(property.stats.totalViews)} Views
        </button>
      </Item>,
    );
  }

  return items;
};

const PropertyInfoBar: FunctionComponent<Props> = (props) => {
  const items = getItems(props.property, props.onClick);

  if (props.property.type === 'development') {
    return null;
  }

  return <Container className={`total-items-${items.length}`}>{items}</Container>;
};

export default PropertyInfoBar;
